import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, ChevronRight, UserCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const fold = (s) => (s || "").toLocaleLowerCase("tr");

export default function CommitteePicker({ onPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/election-day/committees")
      .then(({ data }) => setRows(data.committees))
      .catch((e) => { setRows([]); toast.error(formatApiError(e.response?.data?.detail, e)); });
  }, []);

  if (!rows) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Komiteler yükleniyor...</div>;

  const list = q.trim()
    ? rows.filter((c) => fold(c.name).includes(fold(q)) || String(c.committee_no) === q.trim())
    : rows;

  return (
    <div className="space-y-4" data-testid="committee-picker">
      <div className="relative sm:max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara"
          className="pl-8" data-testid="picker-search" />
      </div>
      {list.length === 0 && (
        <Card className="p-6 text-center text-muted-foreground" data-testid="picker-empty">
          Yetkili olduğunuz komite bulunamadı.
        </Card>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => {
          const pct = c.faal ? Math.round(c.marked / c.faal * 100) : 0;
          return (
            <button key={c.committee_no} type="button" onClick={() => onPick(c.committee_no)}
              data-testid={`picker-committee-${c.committee_no}`}
              className="rounded-xl border border-border bg-card p-4 text-left transition-all hover:border-[#0F2C59] hover:shadow-md active:scale-[0.99]">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display font-bold text-base leading-tight">
                    {c.committee_no}. {c.name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Oy kullanan <b className="text-foreground">{c.marked}</b> / {c.faal} FAAL · %{pct}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </div>
              <Progress value={pct} className="h-1.5 mt-3" />
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {c.is_officer && (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">
                    <UserCheck className="h-3 w-3 mr-1" /> Görevlisiyim
                  </Badge>
                )}
                {c.officers?.length
                  ? <span className="text-[11px] text-muted-foreground truncate">Görevli: {c.officers.join(", ")}</span>
                  : <span className="text-[11px] text-muted-foreground">Görevli atanmadı</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
